import { Box, Typography } from '@mui/material';
import { FormikProps, useFormik } from 'formik';
import { forwardRef, Ref, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import * as yup from 'yup';

import { DateTimePickerButton } from '@/components/common/DateTimePickerButton';
import { Field, TextInput } from '@/components/ds';
import { CalendarEvent, CreateCalendarEvent, UpdateCalendarEvent } from '@/models/CalendarEntry';
import { useTranslationPrefix } from '@/translations/i18n';

export type EditCalendarEventFormValues = CreateCalendarEvent | UpdateCalendarEvent;

export type EditCalendarEventFormRef<T extends EditCalendarEventFormValues> = FormikProps<T>;

export const EVENT_PALETTE = ['#c0ff02', '#38bdf8', '#fb923c', '#f472b6', '#a78bfa', '#34d399'];

export interface EditCalendarEventFormProps<T extends EditCalendarEventFormValues> {
  /**
   * The event to be edited. Should not be provided when creating an event.
   */
  event: T extends CreateCalendarEvent
    ? undefined
    : T extends UpdateCalendarEvent
    ? CalendarEvent | null
    : never;
  /**
   * Callback fired when the form is submitted.
   */
  onSubmit: (values: T) => void;
  /** Currently selected color swatch (hex). Managed externally. */
  selectedColor?: string;
  /** Called when the user picks a color swatch. */
  onColorChange?: (color: string) => void;
}

const FONT = "'Manrope', sans-serif";


const EditCalendarEventFormComponent = <T extends EditCalendarEventFormValues>(
  { event, onSubmit, selectedColor, onColorChange }: EditCalendarEventFormProps<T>,
  ref: Ref<EditCalendarEventFormRef<T>>,
) => {
  const t = useTranslationPrefix('home.calendar.events.edit_form');
  const validationT = useTranslationPrefix('validation');

  const initialValues = useMemo(
    () =>
      ({
        name: event?.name ?? '',
        startDate: event?.startDate,
        endDate: event?.endDate,
      } as T),
    [event],
  );

  const validationSchema = yup.object({
    name: yup.string().required(validationT('required', { name: t('name') })),
    startDate: yup.number().required(validationT('required', { name: t('start_date') })),
    endDate: yup
      .number()
      .required(validationT('required', { name: t('end_date') }))
      .min(
        yup.ref('startDate'),
        validationT('after', { name: t('end_date'), after: t('start_date') }),
      ),
  });

  const formik = useFormik<T>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    onSubmit,
  });

  const [startPickerDate, setStartPickerDate] = useState<Date | null>(null);
  const [startPickerTime, setStartPickerTime] = useState<Date | null>(null);
  const [endPickerDate, setEndPickerDate] = useState<Date | null>(null);
  const [endPickerTime, setEndPickerTime] = useState<Date | null>(null);

  useEffect(() => {
    const ts = formik.values.startDate;
    if (!ts) {
      setStartPickerDate(null);
      setStartPickerTime(null);
      return;
    }
    const d = new Date(ts * 1000);
    setStartPickerDate(d);
    setStartPickerTime(d.getHours() || d.getMinutes() ? d : null);
  }, [formik.values.startDate]);

  useEffect(() => {
    const ts = formik.values.endDate;
    if (!ts) {
      setEndPickerDate(null);
      setEndPickerTime(null);
      return;
    }
    const d = new Date(ts * 1000);
    setEndPickerDate(d);
    setEndPickerTime(d.getHours() || d.getMinutes() ? d : null);
  }, [formik.values.endDate]);

  const handleStartConfirm = (date: Date | null, time: Date | null) => {
    setStartPickerDate(date);
    setStartPickerTime(time);
    if (!date) {
      formik.setFieldValue('startDate', undefined);
      return;
    }
    const combined = new Date(date);
    if (time) combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
    formik.setFieldValue('startDate', Math.floor(combined.getTime() / 1000));
  };

  const handleEndConfirm = (date: Date | null, time: Date | null) => {
    setEndPickerDate(date);
    setEndPickerTime(time);
    if (!date) {
      formik.setFieldValue('endDate', undefined);
      return;
    }
    const combined = new Date(date);
    if (time) combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
    formik.setFieldValue('endDate', Math.floor(combined.getTime() / 1000));
  };

  useImperativeHandle(ref, () => formik);

  const error = (msg?: unknown) =>
    msg ? (
      <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#DB5282', mt: '6px', fontFamily: FONT }}>
        {msg as string}
      </Typography>
    ) : null;

  return (
    <form onSubmit={formik.handleSubmit}>
      {/* labelled fields, the house way: a label over each control, the two times side by side, the colour as a
          visible row of swatches rather than a square hidden in the name field */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', minWidth: 0 }}>
        <Field label={t('name')} htmlFor="calendar-event-name">
          <TextInput
            id="calendar-event-name"
            autoFocus
            placeholder="What's happening?"
            value={formik.values.name}
            onChange={(e) => formik.setFieldValue('name', e.target.value)}
            onBlur={() => formik.setFieldTouched('name', true)}
          />
          {formik.touched.name && error(formik.errors.name)}
        </Field>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '12px' }}>
          <Field label={t('start_date')}>
            <DateTimePickerButton
              date={startPickerDate}
              time={startPickerTime}
              placeholder="Pick a date & time"
              onConfirm={handleStartConfirm}
            />
            {formik.touched.startDate && error(formik.errors.startDate)}
          </Field>
          <Field label={t('end_date')}>
            <DateTimePickerButton
              date={endPickerDate}
              time={endPickerTime}
              placeholder="Pick a date & time"
              onConfirm={handleEndConfirm}
            />
            {formik.touched.endDate && error(formik.errors.endDate)}
          </Field>
        </Box>

        {onColorChange && (
          <Field label="Colour">
            <Box role="radiogroup" aria-label="Event colour" sx={{ display: 'flex', gap: '10px' }}>
              {EVENT_PALETTE.map((c) => {
                const on = selectedColor === c;
                return (
                  <Box
                    key={c}
                    component="button"
                    type="button"
                    role="radio"
                    aria-checked={on}
                    aria-label={c}
                    onClick={() => onColorChange(c)}
                    sx={{
                      width: 24,
                      height: 24,
                      p: 0,
                      border: 0,
                      borderRadius: '7px',
                      background: c,
                      cursor: 'pointer',
                      // the chosen swatch is ringed off the panel; the rest stay quiet until hovered
                      boxShadow: on ? `0 0 0 2px var(--tx-glass-panel), 0 0 0 4px ${c}` : 'none',
                      opacity: selectedColor && !on ? 0.55 : 1,
                      transition: 'opacity 0.12s, box-shadow 0.12s',
                      '&:hover': { opacity: 1 },
                    }}
                  />
                );
              })}
            </Box>
          </Field>
        )}
      </Box>
    </form>
  );
};

export const EditCalendarEventForm = forwardRef(EditCalendarEventFormComponent) as <
  T extends EditCalendarEventFormValues,
>(
  props: EditCalendarEventFormProps<T> & { ref?: Ref<EditCalendarEventFormRef<T>> },
) => ReturnType<typeof EditCalendarEventFormComponent<T>>;
